import { useMutation, useQuery } from '@apollo/client';
import type { RelatedEntity, User } from '@interfaces/user';
import { getWorkflowCompletionHeaders } from '@ovComponents/4-module/workflowCompletion/workflowCompletionHeaders';
import {
  FETCH_USER_RELATED_ENTITIES,
  UPDATE_USER_RELATED_ENTITIES,
} from '@pages/client/components/relatedEntitiesQueries';
import { useGlobalToast } from '@providers/globalToastProvider';
import { usePermissions } from '@providers/userContextProvider';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RelatedEntitiesVisual } from './relatedEntities.visual';

export const RelatedEntities = ({
  options,
  workflowCompletion,
  userId,
  onNext,
  stepLoading,
}: {
  options: any;
  workflowCompletion?: any;
  userId: string;
  onNext: () => void;
  stepLoading: boolean;
}) => {
  const { t } = useTranslation('workflowCompletions');
  const { showToast } = useGlobalToast();
  const { permissions } = usePermissions();
  const [user, setUser] = useState<User>();

  const { loading: fetchUserLoading } = useQuery(
    FETCH_USER_RELATED_ENTITIES(permissions),
    {
      variables: { userId },
      fetchPolicy: 'no-cache',
      onCompleted: (data) => setUser(data.fetchUser.user),
    },
  );
  const [
    updateUserRelatedEntities,
    { loading: updateUserRelatedEntitiesLoading },
  ] = useMutation(UPDATE_USER_RELATED_ENTITIES, {
    context: {
      headers: getWorkflowCompletionHeaders(workflowCompletion),
    },
    refetchQueries: [FETCH_USER_RELATED_ENTITIES(permissions)],
  });

  const handleDraftSubmission = async (draftRelatedEntity: RelatedEntity) => {
    const existingRelatedEntitiesInputs =
      user?.relatedEntities?.map((relEnt) => ({
        entityId: relEnt.entity.id,
        relation: relEnt.relation,
      })) ?? [];
    if (draftRelatedEntity) {
      await updateUserRelatedEntities({
        variables: {
          userId,
          relatedEntities: [
            ...existingRelatedEntitiesInputs,
            {
              entityId: draftRelatedEntity.entity.id,
              relation: draftRelatedEntity.relation,
            },
          ],
        },
        onCompleted: () => {
          showToast({
            severity: 'success',
            message: t(
              'workflowCompletions:relatedEntities.newRelatedEntitySuccess',
            ),
          });
        },
      });
    } else {
      await updateUserRelatedEntities({
        variables: {
          userId,
          relatedEntities: [...existingRelatedEntitiesInputs],
        },
        onCompleted: () => {
          showToast({
            severity: 'success',
            message: t(
              'workflowCompletions:relatedEntities.newRelatedEntitySuccess',
            ),
          });
        },
      });
    }
  };

  const loading =
    stepLoading || fetchUserLoading || updateUserRelatedEntitiesLoading;

  return (
    <RelatedEntitiesVisual
      user={user}
      options={options}
      loading={loading}
      onContinue={onNext}
      onDraftSubmission={handleDraftSubmission}
    />
  );
};
